"use client"

import { format, parse } from "date-fns"
import * as React from "react"

import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { CalendarIcon } from "lucide-react"

interface BirthdatePickerProps {
  onDateChange: (date: Date) => void
  value: Date
  isProfileUpdate?: boolean
}

export function BirthdatePicker({ onDateChange, value, isProfileUpdate = false }: BirthdatePickerProps) {
  const [inputValue, setInputValue] = React.useState(() => (isProfileUpdate ? format(value, "dd/MM/yyyy") : ""))

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "") // Remove non-digits

    if (value.length > 8) return // Max 8 digits (DDMMYYYY)

    // Handle days (max 30)
    if (value.length >= 2) {
      const days = parseInt(value.slice(0, 2))
      if (days > 31) {
        value = "31" + value.slice(2)
      } else if (days === 0) {
        value = "01" + value.slice(2)
      }
      value = value.slice(0, 2) + "/" + value.slice(2)
    }

    // Handle months (max 12)
    if (value.length >= 5) {
      const months = parseInt(value.slice(3, 5))
      if (months > 12) {
        value = value.slice(0, 3) + "12" + value.slice(5)
      } else if (months === 0) {
        value = value.slice(0, 3) + "01" + value.slice(5)
      }
      value = value.slice(0, 5) + "/" + value.slice(5)
    }

    // Handle years (between currentYear-32 and currentYear-18)
    if (value.length === 10) {
      const currentYear = new Date().getFullYear()
      const minYear = currentYear - 35
      const maxYear = currentYear - 18
      const year = parseInt(value.slice(6, 10))

      if (year < minYear) {
        value = value.slice(0, 6) + minYear.toString()
      } else if (year > maxYear) {
        value = value.slice(0, 6) + maxYear.toString()
      }
    }

    setInputValue(value)

    // Try to parse the date if we have a complete value
    if (value.length === 10) {
      try {
        const parsedDate = parse(value, "dd/MM/yyyy", new Date())
        if (!isNaN(parsedDate.getTime())) {
          // Validate date is within bounds
          onDateChange(parsedDate)
        }
      } catch (error) {
        // Invalid date format
        console.error("Invalid date format")
      }
    }
  }

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget
    const value = input.value

    // Move cursor after slash when typing
    if (value.length === 2 && !value.includes("/")) {
      input.selectionStart = 3
      input.selectionEnd = 3
    } else if (value.length === 5 && value.split("/").length === 2) {
      input.selectionStart = 6
      input.selectionEnd = 6
    }
  }

  return (
    <div className="relative">
      <Input
        type="text"
        placeholder="DD/MM/YYYY"
        value={inputValue}
        onChange={handleInputChange}
        onKeyUp={handleKeyUp}
        className={cn("font-normal pl-10", !inputValue && "text-muted-foreground")}
        maxLength={10}
      />
      <CalendarIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
    </div>
  )
}
